import GameStore from './store.js';
import { qs, qsa, delegate, toggleHidden } from './utils/dom.js';
import debounce from './utils/debounce.js';
import SearchOverlay from './components/search-overlay.js';
import { renderGames } from './components/game-grid.js';
import { trackEvent } from './utils/analytics.js';
import { toggleFavorite, isFavorite } from './utils/storage.js';

const initCardRails = () => {
  const rails = qsa('[data-card-rail]');
  if (!rails.length) return;

  rails.forEach(rail => {
    const track = rail.querySelector('[data-rail-track]') || rail.querySelector('.game-rail__inner');
    const prevWrap = rail.querySelector('[data-rail-prev]');
    const nextWrap = rail.querySelector('[data-rail-next]');
    const prevBtn = prevWrap?.querySelector('button');
    const nextBtn = nextWrap?.querySelector('button');
    if (!track || !prevBtn || !nextBtn) return;

    const getStep = () => {
      const cards = track.querySelectorAll('.game-card');
      if (cards.length < 2) {
        return Math.max(track.clientWidth * 0.9, 240);
      }
      const first = cards[0].getBoundingClientRect();
      const second = cards[1].getBoundingClientRect();
      const cardSpan = second.left - first.left;
      if (cardSpan <= 0) return Math.max(track.clientWidth * 0.9, 240);
      const visible = Math.max(1, Math.floor(track.clientWidth / cardSpan));
      return cardSpan * visible;
    };

    const updateNav = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const hasOverflow = maxScroll > 4;
      prevWrap?.classList.toggle('is-hidden', !hasOverflow);
      nextWrap?.classList.toggle('is-hidden', !hasOverflow);
      if (!hasOverflow) {
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        prevWrap?.classList.add('is-disabled');
        nextWrap?.classList.add('is-disabled');
        return;
      }

      const atStart = track.scrollLeft <= 4;
      prevBtn.disabled = atStart;
      prevWrap?.classList.toggle('is-disabled', atStart);
      const atEnd = track.scrollLeft >= maxScroll - 4;
      nextBtn.disabled = atEnd;
      nextWrap?.classList.toggle('is-disabled', atEnd);
    };

    const handleNav = direction => {
      const step = getStep();
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const nextPosition = Math.min(Math.max(track.scrollLeft + direction * step, 0), maxScroll);
      track.scrollTo({ left: nextPosition, behavior: 'smooth' });
    };

    prevBtn.addEventListener('click', () => handleNav(-1));
    nextBtn.addEventListener('click', () => handleNav(1));

    const debouncedResize = debounce(updateNav, 150);
    track.addEventListener('scroll', updateNav, { passive: true });
    window.addEventListener('resize', debouncedResize);
    updateNav();
  });
};

const filterGamesByQuery = (games = [], query = '') => {
  const token = query.trim().toLowerCase();
  if (!token) return [];
  return games.filter(game => {
    const haystack = [
      game.title,
      game.provider,
      ...(game.tags || []),
      ...(game.categories || []),
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(token);
  });
};

const formatCountLabel = count => {
  if (count === 0) return 'No games found';
  if (count === 1) return '1 game found';
  return `${count} games found`;
};

const parseJson = id => {
  const el = qs(`#${id}`);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (err) {
    console.error(`Failed to parse JSON from #${id}`, err);
    return null;
  }
};

const initNavigation = () => {
  const toggle = qs('[data-nav-toggle]');
  const sidebar = qs('[data-sidebar]');
  const closeBtn = qs('[data-close-sidebar]');
  const shellMain = qs('[data-shell-main]');
  if (!toggle || !sidebar) return;

  const setOpen = open => {
    sidebar.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    document.body.classList.toggle('sidebar-open', open);
  };

  toggle.addEventListener('click', () => {
    const isOpen = sidebar.classList.contains('is-open');
    setOpen(!isOpen);
  });

  closeBtn?.addEventListener('click', () => setOpen(false));

  shellMain?.addEventListener('click', event => {
    if (!sidebar.classList.contains('is-open')) return;
    if (toggle.contains(event.target)) return;
    if (!sidebar.contains(event.target)) {
      setOpen(false);
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && sidebar.classList.contains('is-open')) {
      setOpen(false);
    }
  });
};

const updateActiveChip = (chips, value) => {
  chips.forEach(chip => {
    const isActive = chip.dataset.filterValue === value;
    chip.classList.toggle('is-active', isActive);
    chip.setAttribute('aria-checked', String(isActive));
  });
};

const updateUrl = (state, options = {}) => {
  const { skipCategory = false } = options;
  const url = new URL(window.location.href);
  if (skipCategory) {
    url.searchParams.delete('category');
  } else if (state.category && state.category !== 'all') {
    url.searchParams.set('category', state.category);
  } else {
    url.searchParams.delete('category');
  }

  if (state.search) {
    url.searchParams.set('q', state.search);
  } else {
    url.searchParams.delete('q');
  }

  if (state.sort && state.sort !== 'latest') {
    url.searchParams.set('sort', state.sort);
  } else {
    url.searchParams.delete('sort');
  }

  if (state.page > 1) {
    url.searchParams.set('page', String(state.page));
  } else {
    url.searchParams.delete('page');
  }

  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, '', next);
};

const initListingPage = games => {
  const grid = qs('[data-game-grid]');
  if (!grid) return;

  const filterGroup = qs('[data-filter-categories]');
  const sortSelect = qs('[data-sort-select]');
  const inlineSearch = qs('[data-inline-search]');
  const loadMoreBtn = qs('[data-load-more]');
  const emptyState = qs('[data-empty-state]');
  const resetBtn = qs('[data-reset-filters]');
  const categorySection = qs('[data-initial-category]');

  const perPage = 24;
  const params = new URLSearchParams(window.location.search);
  const categoryParam = params.get('category');
  const sortParam = params.get('sort');
  const searchParam = params.get('q');
  const pageParam = Number.parseInt(params.get('page'), 10);
  const initialCategory = categorySection
    ? categorySection.dataset.initialCategory
    : categoryParam || 'all';
  const isCategoryPage = Boolean(categorySection);
  const store = new GameStore(games, {
    category: initialCategory,
    perPage,
    sort: sortParam || 'latest',
    search: searchParam || '',
    page: Number.isNaN(pageParam) ? 1 : Math.max(pageParam, 1),
  });

  const chips = qsa('[data-filter-value]', filterGroup || document);
  updateActiveChip(chips, initialCategory || 'all');

  const overlay = new SearchOverlay(document, {
    onSearch: query => {
      if (inlineSearch) inlineSearch.value = query;
      store.setSearch(query);
    },
  });

  store.subscribe(snapshot => {
    const { state, visible, total, hasMore } = snapshot;
    renderGames(grid, visible);

    toggleHidden(emptyState, total !== 0);
    if (loadMoreBtn) {
      toggleHidden(loadMoreBtn, !hasMore);
    }

    if (inlineSearch && inlineSearch.value !== state.search) {
      inlineSearch.value = state.search;
    }
    overlay?.setValue?.(state.search);
    if (sortSelect && sortSelect.value !== state.sort) {
      sortSelect.value = state.sort;
    }
    updateActiveChip(chips, state.category);
    updateUrl(state, { skipCategory: isCategoryPage });
  });

  if (filterGroup) {
    delegate(filterGroup, '[data-filter-value]', 'click', (event, target) => {
      event.preventDefault();
      const value = target.dataset.filterValue;
      store.setCategory(value);
      trackEvent('filter_category', { category: value });
    });
  }

  if (sortSelect) {
    sortSelect.addEventListener('change', event => {
      store.setSort(event.target.value);
      trackEvent('sort_change', { sort: event.target.value });
    });
  }

  if (inlineSearch) {
    inlineSearch.addEventListener(
      'input',
      debounce(event => {
        store.setSearch(event.target.value.trim());
      }, 250)
    );
  }

  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      store.resetFilters();
      trackEvent('filters_reset');
    });
  }

  if (loadMoreBtn) {
    loadMoreBtn.addEventListener('click', () => {
      store.loadMore();
      trackEvent('load_more');
    });
  }
};

const initSearchPage = games => {
  const grid = qs('[data-search-grid]');
  if (!grid) return;

  const summary = qs('[data-search-summary]');
  const title = qs('[data-search-title]');
  const countEl = qs('[data-search-count]');
  const emptyState = qs('[data-search-empty]');
  const emptyDefault = qs('[data-search-empty-default]');
  const emptyNoResults = qs('[data-search-empty-noresults]');
  const termEl = qs('[data-search-term]');

  const applyQuery = () => {
    const params = new URLSearchParams(window.location.search);
    const query = params.get('q')?.trim() || '';
    const matches = filterGamesByQuery(games, query);

    if (termEl) termEl.textContent = query;

    if (title) {
      title.textContent = query ? `Results for “${query}”` : 'Search the catalog';
    }

    if (summary) {
      summary.textContent = query
        ? `Search results for “${query}”.`
        : 'Enter a term to explore games.';
    }

    if (countEl) {
      countEl.textContent = query ? formatCountLabel(matches.length) : '';
    }

    if (!query) {
      renderGames(grid, []);
      toggleHidden(emptyState, false);
      toggleHidden(emptyDefault, false);
      toggleHidden(emptyNoResults, true);
      return;
    }

    if (matches.length === 0) {
      renderGames(grid, []);
      toggleHidden(emptyState, false);
      toggleHidden(emptyDefault, true);
      toggleHidden(emptyNoResults, false);
      return;
    }

    renderGames(grid, matches);
    toggleHidden(emptyState, true);
  };

  applyQuery();
  window.addEventListener('popstate', applyQuery);
};

const initGameDetail = game => {
  const favoritesBtn = qs('[data-favorite-toggle]');
  const label = qs('[data-favorite-label]');
  const shareBtn = qs('[data-share-game]');

  if (favoritesBtn && label) {
    const updateState = () => {
      const active = isFavorite(game.slug);
      favoritesBtn.classList.toggle('is-active', active);
      label.textContent = active ? 'Saved' : 'Save Game';
    };

    favoritesBtn.addEventListener('click', () => {
      toggleFavorite(game.slug);
      updateState();
      trackEvent('favorite_toggle', { slug: game.slug });
    });

    updateState();
  }

  if (shareBtn) {
    shareBtn.addEventListener('click', async () => {
      const shareData = {
        title: game.title,
        text: game.description,
        url: window.location.href,
      };
      if (navigator.share) {
        try {
          await navigator.share(shareData);
          trackEvent('share_native', { slug: game.slug });
        } catch (err) {
          console.warn('Share cancelled', err);
        }
      } else if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(shareData.url);
          shareBtn.textContent = 'Link copied';
          setTimeout(() => {
            shareBtn.textContent = 'Share with Friends';
          }, 2000);
          trackEvent('share_clipboard', { slug: game.slug });
        } catch (err) {
          console.error('Clipboard failed', err);
        }
      }
    });
  }
};

const bootstrap = () => {
  initNavigation();
  initCardRails();

  const gamesData = parseJson('all-games');
  if (Array.isArray(gamesData) && gamesData.length) {
    initListingPage(gamesData);
    initSearchPage(gamesData);
  }

  const gameDetail = parseJson('game-detail');
  if (gameDetail) {
    initGameDetail(gameDetail);
  }
};

document.addEventListener('DOMContentLoaded', bootstrap);
